import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Pencil,
  Trash2,
  ArrowRight,
  Loader2,
  AlertCircle,
  Inbox,
} from 'lucide-react';

import EditorStatCard from './EditorStatCard';
import ReviewModal from './ReviewModal';
import PendingArticleTable from './PendingArticleTable';
import NotificationBell from './Notification';

import {
  fetchNewsForDashboard,
  updateNewsStatus,
  deleteNews,
} from '../../utils/api/newsApi';

import {
  fetchNotifications,
  markNotificationRead,
} from '../../utils/api/notificationApi';

const content = {
  ne: {
    welcome: 'स्वागत छ सम्पादक',
    instruction: 'रिपोर्टरहरूले पेश गरेका समाचार समीक्षा, प्रकाशन र व्यवस्थापन गर्नुहोस्।',
    stats: {
      pending: 'समीक्षा बाँकी',
      published: 'प्रकाशित लेखहरू',
      rejected: 'अस्वीकृत लेखहरू',
      total: 'कुल लेखहरू',
    },
    pendingTitle: 'समीक्षाको लागि बाँकी लेखहरू',
    pendingSubtitle: 'पहिले आएको पहिले समीक्षा गर्नुहोस्',
    viewAll: 'सबै हेर्नुहोस्',
    empty: 'अहिले समीक्षाको लागि कुनै लेख छैन',
    emptySubtitle: 'नयाँ लेख पेश हुँदा यहाँ देखा पर्नेछ',
    retry: 'फेरि प्रयास गर्नुहोस्',
  },
  en: {
    welcome: 'Welcome',
    instruction: "Review, publish, and manage news submitted by reporters.",
    stats: {
      pending: 'Pending Review',
      published: 'Published Articles',
      rejected: 'Rejected Articles',
      total: 'Total Articles',
    },
    pendingTitle: 'Articles Awaiting Review',
    pendingSubtitle: 'Oldest submissions first',
    viewAll: 'View All',
    empty: 'No articles pending review',
    emptySubtitle: 'New submissions from reporters will show up here',
    retry: 'Retry',
  },
};

const StatSkeleton = () => (
  <div className="animate-pulse rounded-3xl border border-char-100 bg-white p-5">
    <div className="h-10 w-10 rounded-2xl bg-char-100" />
    <div className="mt-4 h-7 w-12 rounded bg-char-100" />
    <div className="mt-2 h-4 w-24 rounded bg-char-100" />
  </div>
);

const TableSkeleton = () => (
  <div className="overflow-hidden rounded-3xl border border-char-100 bg-white">
    <div className="border-b border-char-100 bg-char-50/60 px-5 py-3.5">
      <div className="h-3 w-32 animate-pulse rounded bg-char-100" />
    </div>
    {[...Array(4)].map((_, i) => (
      <div key={i} className="flex items-center gap-4 border-b border-char-50 px-5 py-4 last:border-0">
        <div className="h-4 w-1/3 animate-pulse rounded bg-char-100" />
        <div className="h-5 w-16 animate-pulse rounded-full bg-char-100" />
        <div className="h-4 w-20 animate-pulse rounded bg-char-100" />
        <div className="ml-auto h-4 w-16 animate-pulse rounded bg-char-100" />
      </div>
    ))}
  </div>
);

const EditorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { language = "ne" } = useOutletContext() || {};
  const t = content[language] || content.en;

  const [stats, setStats] = useState({
    pendingReview: 0,
    publishedArticles: 0,
    rejectedArticles: 0,
    totalArticles: 0,
  });
  const [pendingArticles, setPendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Real notifications, fetched from /api/notifications.
  const [notifications, setNotifications] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNewsForDashboard({ status: 'pending', page: 1, limit: 20 });
      const counts = res?.counts || {};
      setStats({
        pendingReview: counts.pending ?? 0,
        publishedArticles: counts.published ?? 0,
        rejectedArticles: counts.rejected ?? 0,
        totalArticles:
          (counts.pending ?? 0) + (counts.published ?? 0) + (counts.rejected ?? 0),
      });
      setPendingArticles(res?.data ?? []);
    } catch (err) {
      setError(err?.message || 'Failed to load editor dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await fetchNotifications();
      setNotifications(res?.data ?? []);
    } catch (err) {
      console.error('Failed to load notifications', err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    loadNotifications();
  }, [fetchDashboardData, loadNotifications]);

  const handleApprove = async (articleId) => {
    setActionLoadingId(articleId);
    try {
      await updateNewsStatus(articleId, 'published');
      setPendingArticles((prev) => prev.filter((a) => (a._id ?? a.id) !== articleId));
      setStats((prev) => ({
        ...prev,
        pendingReview: Math.max(0, (prev.pendingReview || 0) - 1),
        publishedArticles: (prev.publishedArticles || 0) + 1,
      }));
      setActiveArticle(null);
      // Backend creates the reporter-facing "published" notification itself
      // (see updateNewsStatus in newsController.js) — no local mock needed here.
    } catch (err) {
      setError(err?.message || 'Failed to approve article');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (articleId, remarks) => {
    if (!remarks?.trim()) {
      setError('Remarks are required when rejecting an article');
      return;
    }
    setActionLoadingId(articleId);
    try {
      await updateNewsStatus(articleId, 'rejected', remarks);
      setPendingArticles((prev) => prev.filter((a) => (a._id ?? a.id) !== articleId));
      setStats((prev) => ({
        ...prev,
        pendingReview: Math.max(0, (prev.pendingReview || 0) - 1),
        rejectedArticles: (prev.rejectedArticles || 0) + 1,
      }));
      setActiveArticle(null);
    } catch (err) {
      setError(err?.message || 'Failed to reject article');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (articleId) => {
    setActionLoadingId(articleId);
    try {
      await deleteNews(articleId);
      setPendingArticles((prev) => prev.filter((a) => (a._id ?? a.id) !== articleId));
      setStats((prev) => ({
        ...prev,
        pendingReview: Math.max(0, (prev.pendingReview || 0) - 1),
        totalArticles: Math.max(0, (prev.totalArticles || 0) - 1),
      }));
      setActiveArticle(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete article');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleMarkNotificationRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
    try {
      await markNotificationRead(id);
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  };

  const handleMarkAllNotificationsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  

  return (
    <div className="w-full space-y-8 overflow-x-hidden">
      {/* Welcome + Notifications */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div
          className="relative flex-1 overflow-hidden rounded-3xl bg-gradient-to-br from-primary-500 to-primary-600 p-6 text-white shadow-green sm:p-8"
          style={{
            backgroundImage: 'linear-gradient(to bottom right, #127536, #15803d)',
            boxShadow: '0 10px 25px -5px rgba(22,163,74,0.25)',
          }}
        >
          <div
            className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10 blur-2xl"
            aria-hidden="true"
          />
          <h2 className="relative text-2xl font-extrabold text-white sm:text-3xl">
            {t.welcome}
            {user?.name && (
              <span className="block text-lg font-bold text-white/80 sm:inline sm:text-2xl">
                , {user.name}
              </span>
            )}
          </h2>
          <p className="relative mt-2 max-w-xl text-sm leading-relaxed text-white/80">
            {t.instruction}
          </p>
        </div>

        <NotificationBell
          notifications={notifications}
          onMarkRead={handleMarkNotificationRead}
          onMarkAllRead={handleMarkAllNotificationsRead}
          language={language}
        />
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={fetchDashboardData}
            className="flex-shrink-0 rounded-lg px-2 py-0.5 underline underline-offset-2 hover:bg-red-100"
          >
            {t.retry}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          [...Array(4)].map((_, i) => <StatSkeleton key={i} />)
        ) : (
          <>
            <EditorStatCard icon={Clock} label={t.stats.pending} value={stats.pendingReview} color="amber" />
            <EditorStatCard icon={CheckCircle} label={t.stats.published} value={stats.publishedArticles} color="blue" />
            <EditorStatCard icon={XCircle} label={t.stats.rejected} value={stats.rejectedArticles} color="red" />
            <EditorStatCard icon={FileText} label={t.stats.total} value={stats.totalArticles} color="green" />
          </>
        )}
      </div>

      {/* Pending Review Queue */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-extrabold text-char-900">{t.pendingTitle}</h3>
            {!loading && pendingArticles.length > 0 && (
              <p className="mt-0.5 text-xs text-char-400">{t.pendingSubtitle}</p>
            )}
          </div>
          <button
            onClick={() => navigate('/editor/articles')}
            className="flex items-center gap-1 text-xs font-extrabold uppercase tracking-wider text-primary transition-colors hover:text-primary-300"
          >
            {t.viewAll}
            <ArrowRight size={14} />
          </button>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : pendingArticles.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-3xl border border-dashed border-char-200 bg-white py-12">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-char-50 text-char-300">
              <Inbox size={22} />
            </span>
            <p className="text-sm font-bold text-char-500">{t.empty}</p>
            <p className="text-xs text-char-400">{t.emptySubtitle}</p>
          </div>
        ) : (
          <PendingArticleTable
            articles={pendingArticles}
            language={language}
            actionLoadingId={actionLoadingId}
            onReview={(article) => setActiveArticle(article)}
            onEdit={(article) => navigate('/editor/create-article', { state: { article } })}
            onDelete={(article) => setActiveArticle({ ...article, mode: 'delete' })}
          />
        )}
      </div>

      {activeArticle && (
        <ReviewModal
          article={activeArticle}
          language={language}
          loading={actionLoadingId === (activeArticle._id ?? activeArticle.id)}
          onApprove={() => handleApprove(activeArticle._id ?? activeArticle.id)}
          onReject={(remarks) => handleReject(activeArticle._id ?? activeArticle.id, remarks)}
          onDelete={
            activeArticle.mode === 'delete'
              ? () => handleDelete(activeArticle._id ?? activeArticle.id)
              : undefined
          }
          onClose={() => setActiveArticle(null)}
        />
      )}
    </div>
  );
};

export default EditorDashboard;